import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import { Box, Flex, Inline } from '@atlaskit/primitives/compiled';
import GenericTile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';

import { ReturnIcon } from '../../../../common/constants/icons/return';
import { SearchResult } from '../../../../common/ui/quick-find/search-dialog-result';
import type { FilterSuggestionGroup } from '../../../../common/ui/quick-find/types';
import { type State } from '../../../../common/utils/quick-find/state';
import { useBootstrap } from '../../../../controllers/store';

import { messages } from './messages';

const styles = cssMap({
	filterBadge: {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
});

type Props = {
	result: FilterSuggestionGroup;
	state: State;
};

export const FilterSuggestion = ({ result }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [{ queryParams }, { setQueryParams }] = useBootstrap();

	const onResultClicked = useCallback(() => {
		const params = Object.fromEntries(
			Object.entries(result).map(([key, value]) => [key, value.option.value]),
		);
		setQueryParams({ ...queryParams, ...params });
		// fire analytics
	}, [result, setQueryParams, queryParams]);

	const icon = (
		<GenericTile label="" size="small">
			<CustomizeIcon
				label={formatMessage(messages.suggested_filter_icon_label)}
				color="currentColor"
			/>
		</GenericTile>
	);
	const title = useMemo(() => {
		return (
			<Flex columnGap="space.100">
				{Object.entries(result).map(([filterId, filterSuggestion]) => {
					return (
						<Box key={filterId} xcss={styles.filterBadge}>
							{formatMessage(messages[filterSuggestion.filterName])}:{' '}
							{filterSuggestion.option.label}
						</Box>
					);
				})}
			</Flex>
		);
	}, [result, formatMessage]);

	const { rightMetadata, highlightedRightMetadata, hoverRightMetadata } = useMemo(() => {
		const rightMetadata = <Box>{formatMessage(messages.suggested_filter)}</Box>;

		const highlightedRightMetadata = (
			<Inline alignBlock="center" space="space.100">
				<Box>{formatMessage(messages.apply_suggested_filter)}</Box>
				<ReturnIcon />
			</Inline>
		);

		const hoverRightMetadata = (
			<Inline alignBlock="center" space="space.100">
				<Box>{formatMessage(messages.apply_suggested_filter)}</Box>
			</Inline>
		);

		return {
			rightMetadata,
			highlightedRightMetadata,
			hoverRightMetadata,
		};
	}, [formatMessage]);

	return (
		<SearchResult
			resultType="filterSuggestions"
			id="filter-suggestion"
			icon={icon}
			title={title}
			rightMetadata={rightMetadata}
			highlightedRightMetadata={highlightedRightMetadata}
			hoveredRightMetadata={hoverRightMetadata}
			onResultClicked={onResultClicked}
		/>
	);
};
